import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useState,
} from 'react';
import * as z from 'zod';
import {
  Control,
  FieldErrors,
  UseFormClearErrors,
  SubmitHandler,
  useForm,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch,
  UseFormTrigger,
} from 'react-hook-form';
import { redirect, useNavigate } from 'react-router-dom';
import {
  funcionarioSchema,
  FuncionarioSchema,
} from '../components/HirePlanForm/Functionary-steps/data/schema/functionarysSchema';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  billetSchema,
  BilletSchema,
} from '../components/HirePlanForm/Functionary-steps/data/schema/billetSchema';
import axios from 'axios';
import { useHirePlanFormStepStore } from '../stores/useHirePlanFormStepStore';
import {
  usePaymentStore,
  useValorTitularStore,
  useVigenciaStore,
} from '../stores/usePaymentStore';
import { useBoostedPlanDataStore } from '../stores/useBoostedPlanDataStore';
import { useLoadingStore } from '../stores/useLoadingStore';
import { useError } from '../stores/useError';
import { useVidaCountStore } from '../stores/useQuantidadeVidas';
import { useFunctionaryExistsStore } from '../stores/useFunctionaryStore';
import { useResponseStore } from '../stores/useResponseStore';
import { response } from 'express';

type PaymentFormSchema = BilletSchema;
type funcionario = FuncionarioSchema;
interface HirePlanFormContextProps {
  register: UseFormRegister<PaymentFormSchema>;
  control: Control<PaymentFormSchema, any>;
  watch: UseFormWatch<PaymentFormSchema>;
  handleSubmit: UseFormHandleSubmit<PaymentFormSchema>;
  onSubmit: (schema: PaymentFormSchema) => void;
  goToNextForm: (schema: PaymentFormSchema) => void;
  errors: FieldErrors<PaymentFormSchema>;
  clearErrors: UseFormClearErrors<PaymentFormSchema>;
  unregister: UseFormUnregister<PaymentFormSchema>;
  setValue: UseFormSetValue<PaymentFormSchema>;
  funcionario: UseFormRegister<funcionario>;
  watchFunc: UseFormWatch<funcionario>;
  setFuncValue: UseFormSetValue<funcionario>;
  errorsFunc: FieldErrors<funcionario>;
}

const HirePlanFormContext = createContext({} as HirePlanFormContextProps);

interface HirePlanFormProviderProps {
  children: ReactNode;
}

export function IncludeFunctionaryProvider({
  children,
}: HirePlanFormProviderProps): ReactElement {
  const initialPlanData = useBoostedPlanDataStore(state => state.data);
  const goToNextStep = useHirePlanFormStepStore(state => state.goToNextStep);
  const paymentId = usePaymentStore(state => state.id);
  const navigate = useNavigate();
  const qtdVidas = useVidaCountStore(x => x.count);

  const funcionarioExistente = useFunctionaryExistsStore(x => x.existe);
  const plano = usePaymentStore(x => x.id);
  const ValorTitular = useValorTitularStore(x => x.valor);
  const vigencia = useVigenciaStore(x => x.vigencia);
  const setLoading = useLoadingStore(state => state.setLoading);
  const setError = useError(state => state.setError);
  const setTitle = useError(state => state.setErrorTitle);
  const setText = useError(state => state.setErrorText);
  const setResponse = useResponseStore(x => x.setResponse);
  const retorno = useResponseStore(x => x.response);

  interface CheckoutData {
    token?: string;
    responsavelEmpresa?: object;
    empresa?: object;
    vencimentoBoleto?: string;
    funcionarios?: object;
    identidade?: object;
    ComprovanteResi?: object;
    CSMei?: object;
    CNPJCard?: object;
  }
  interface responsavelEmpresa {
    nome?: string;
    cpf?: string;
    dataNascimento?: string;
    email?: string;
    telefone?: string;
  }

  interface Empresa {
    cnpj?: string;
    razaoSocial?: string;
    nomeFantasia?: string;
    inscricaoEstadual?: string;
    quantidadeFuncionarios?: number;
    nomeResponsavel?: string;
    cpfResponsavel?: string;
    email?: string;
    dddFixo?: string;
    telefoneFixo?: string;
    dddCelular?: string;
    celular?: string;
    cep?: string;
    logradouro?: string;
    numero?: number;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    idUf?: number;
    idPatrocinio?: number;
  }

  function getSchemaResolver() {
    return billetSchema;
  }
  const {
    register: funcionarioRegister,
    formState: { errors: funcionarioErrors },
    watch: funcionarioWatch,
    setValue: funcionarioSetValue,
  } = useForm<funcionario>({
    resolver: zodResolver(funcionarioSchema),
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    unregister,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm<z.infer<typeof billetSchema>>({
    resolver: zodResolver(billetSchema),
  });
  const goToNextForm: SubmitHandler<PaymentFormSchema> = () => {
    goToNextStep();
  };
  function formatDateToYYYYMM(dateStr: string): number {
    const [day, month, year] = dateStr.split('/');

    if (!day || !month || !year) {
      throw new Error('Data inválida. Formato esperado: dd/MM/yyyy');
    }

    return Number(`${year}${month.padStart(2, '0')}`);
  }
  const onSubmit: SubmitHandler<PaymentFormSchema> = async data => {
    const MonthYear = formatDateToYYYYMM(vigencia);
    const token = process.env.REACT_APP_TOKEN_S4E_V2;
    const formatDate = (date: string) => {
      const [day, month, year] = date.split('/');
      return `${year}-${month}-${day}`;
    };
    if (!funcionarioExistente) {
      setLoading(true);
      const token = process.env.REACT_APP_TOKEN_S4E_V2;
      const dependentesComTitular = [
        {
          tipo: 1,
          nome: data.user.funcionario.nome,
          cpf: data.user.funcionario.cpf?.replace(/[.-]/g, ''),
          sexo: data.user.funcionario.sexo == '1' ? 0 : 1,
          plano: plano,
          planoValor: String(ValorTitular).replace(',', '.'),
          numeroProposta: '',
          carenciaAtendimento: 0,
          rcaId: 0,
          cd_orientacao_sexual: 0,
          OutraOrientacaoSexual: '',
          cd_ident_genero: 0,
          OutraIdentidadeGenero: '',
          idExterno: '',
          nomeMae: data.user.funcionario.motherName,
          MMYYYY1Pagamento: MonthYear,
          numeroCarteira: '',
          observacaoUsuario: '',
          dataNascimento: formatDate(data.user.funcionario.dtNascimento),
          funcionarioCadastro: 62181, // funcionario Ti
          dataCadastroLoteContrato: '',
        },
        ...(data.newDependents?.map(dependent => ({
          tipo:
            dependent.parentesco == '10' || dependent.parentesco == '3'
              ? 3
              : dependent.parentesco == '11' || dependent.parentesco == '4'
              ? 4
              : 10,
          nome: dependent.nome,
          cpf: dependent.cpf?.replace(/[.-]/g, ''),
          sexo: dependent.sexo == '1' ? 0 : 1,
          plano: plano,
          planoValor: String(ValorTitular).replace(',', '.'),
          numeroProposta: '',
          carenciaAtendimento: 0,
          rcaId: 0,
          cd_orientacao_sexual: 0,
          OutraOrientacaoSexual: '',
          cd_ident_genero: 0,
          OutraIdentidadeGenero: '',
          idExterno: '',
          nomeMae: dependent.motherName,
          MMYYYY1Pagamento: MonthYear,
          numeroCarteira: '',
          observacaoUsuario: '',
          dataNascimento: formatDate(dependent.dtNascimento),
          funcionarioCadastro: 62181, // funcionario Ti
          dataCadastroLoteContrato: '',
        })) || []),
      ];

      const checkoutData = {
        token: token,
        dados: {
          parcelaRetidaComissao: '0',
          incluirMensalidades: '1',
          parceiro: {
            codigo: data.user.codVendedor,
            tipoCobranca: 1,
            adesionista: 0,
            maxMensalidadeId: '1',
          },
          responsavelFinanceiro: {
            codigoContrato: data.user.codEmpresa,
            nome: data.user.funcionario.nome,
            dataNascimento: formatDate(data.user.funcionario.dtNascimento),
            cpf: data.user.funcionario.cpf?.replace(/[.-]/g, ''),
            sexo: data.user.funcionario.sexo == '1' ? 0 : 1,
            identidadeNumero: data.user.funcionario.rg,
            identidadeOrgaoExpeditor: data.user.funcionario.orgaoUF,
            enderecoBoleto: '',
            cd_orientacao_sexual: 0,
            OutraOrientacaoSexual: '',
            cd_ident_genero: 0,
            OutraIdentidadeGenero: '',
            agencia: 0,
            agencia_dv: 0,
            conta: 0,
            conta_dv: 0,
            contaOperacao: 0,
            matricula: data.user.funcionario.matricula,
            dataApresentacao: '',
            diaVencimento: '01',
            tipoPagamento: 529,
            origemVenda: 13,
            observacaoAssociado: '',
            codSistemaExterno: '',
            dataAssinaturaContrato: '2024-12-01',
            numeroProposta: String(Math.floor(Math.random() * 1000000000)),
            senhaAssociado: '',
            endereco: {
              cep: data.user.funcionario.address.cep,
              tipoLogradouro: data.user.funcionario.address.tipoLogradouro,
              logradouro: data.user.funcionario.address.street,
              numero: data.user.funcionario.address.number,
              complemento: data.user.funcionario.address.complement || '',
              bairro: data.user.funcionario.address.bairro,
              municipio: data.user.funcionario.address.municipio,
              uf: data.user.funcionario.address.ufId,
              descricaoUf: data.user.funcionario.address.ufDescription,
            },
            fl_AlteraSituacao: 16,
            contatoResponsavelFinanceiro: [
              {
                tipo: 8,
                dado: data.user.funcionario.Celular,
              },
              {
                tipo: 50,
                dado: data.user.funcionario.email,
              },
            ],
          },
          dependente: dependentesComTitular,
        },
        empresa: '',
      };
      const Url = `${process.env.REACT_APP_OG_API_URL}/include/ByPassPj`;
      const planValue = await axios
        .post(Url, checkoutData, {
          headers: {
            'Content-Type': 'application/json',
          },
        })
        .then(response => {
          setLoading(false);
          setError(false);
          return response.data;
        })
        .catch(error => {
          setLoading(false);
          setError(true);

          if (error.response) {
            console.log('Erros: ', error.response.data);

            if (error.response.data.code) {
              setTitle(error.response.data.code);
              setText(error.response.data.error);
              throw new Error(error.response.data.error);
            } else {
              setText(error.response.data.message);
              throw new Error(error.response.data.message);
            }
          } else {
            console.error('Erro de rede ou timeout:', error.message);
            setText('Erro na conexão com o servidor.');
            throw new Error('Erro na conexão com o servidor.');
          }
        })
        .then(() => {
          alert('Cadastro realizado com sucesso!');
          navigate('/');
        });
    } else {
      setLoading(true);
      alert(
        'Erro na inclusão do associadoPJ, Titular já cadastrado e ativo no contrato',
      );
      let newData: any = {};
      try {
        newData = {
          token: token,
          dados: {
            parceiro: {
              codigo: data.user.codVendedor,
              adesionista: 0,
            },
            responsavelFinanceiro: {
              codigo: data.inicial.idFuncionario,
              dataAssinaturaContrato: formatDate(vigencia),
            },
            dependente: data.newDependents?.map(dependent => ({
              tipo:
                dependent.parentesco == '10' || dependent.parentesco == '3'
                  ? 3
                  : dependent.parentesco == '11' || dependent.parentesco == '4'
                  ? 4
                  : 10,
              nome: dependent.nome,
              cpf: dependent.cpf?.replace(/[.-]/g, ''),
              sexo: dependent.sexo == '1' ? 0 : 1,
              plano: plano,
              planoValor: String(ValorTitular).replace(',', '.'),
              numeroProposta: '',
              carenciaAtendimento: 0,
              rcaId: 0,
              cd_orientacao_sexual: 0,
              OutraOrientacaoSexual: '',
              cd_ident_genero: 0,
              OutraIdentidadeGenero: '',
              idExterno: '',
              nomeMae: dependent.motherName,
              MMYYYY1Pagamento: String(MonthYear),
              numeroCarteira: '',
              observacaoUsuario: '',
              dataNascimento: formatDate(dependent.dtNascimento),
              funcionarioCadastro: 0,
              dataCadastroLoteContrato: '',
            })),
            contatoDependente: [],
          },
        };
        console.log('envio: ', newData);
      } catch (error) {
        console.log('Erro ao formatar data:', error);
      }
      const planValue = await fetch(
        `${process.env.REACT_APP_OG_POST_INCLUSAO_DEPENDENTE}es`,
        {
          headers: {
            'content-type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(newData),
        },
      )
        .then(async response => {
          if (response.ok) {
            console.log('Resposta recebida:', response);
            const responseData = await response.json();
            setLoading(false);
            setError(false);
            setResponse(responseData);
            alert('Dependentes incluídos com sucesso!');
            window.location.href = '/';
          }
          const errorData = await response.json();

          if (errorData.code) {
            setTitle(errorData.code);
            setText(errorData.error);
            throw new Error(errorData.error);
          } else {
            setText(errorData.message);
            throw new Error(errorData.message);
          }
        })
        .catch(error => {
          setLoading(false);
          setError(true);
        });
    }
  };

  return (
    <HirePlanFormContext.Provider
      value={{
        register,
        control,
        handleSubmit,
        watch,
        onSubmit,
        goToNextForm,
        errors,
        clearErrors,
        unregister,
        setValue,
        funcionario: funcionarioRegister,
        watchFunc: funcionarioWatch,
        setFuncValue: funcionarioSetValue,
        errorsFunc: funcionarioErrors,
      }}
    >
      {children}
    </HirePlanFormContext.Provider>
  );
}

export function useIncludeFunctionaryForm(): HirePlanFormContextProps {
  return useContext(HirePlanFormContext);
}
